/** @flow */

import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { BoxField, Button, SchemaForm, SwitchButton, Text, View } from "@components";
import { getSchema } from "@utils/schema";
import identityUiSchema from "./identity-ui-schema";
import emailsUiSchema from "./emails-ui-schema";

import type { EmployeeIdentity } from "@modules/service/reducer/employee-identity/types";
import type { EmailControlSettings } from "@modules/service/reducer/email-controls/types";

type Props = {
  ...PropsFromLocalize,
  clientId: number,
  mfaEnabled: boolean,
  identityData: EmployeeIdentity,
  emailControlSettings: ?EmailControlSettings,
  emailSchema: Object,
  onMfaChange: () => void,
  handleIdentitySubmit: (data: {
    clientId: number,
    formData: EmployeeIdentity,
  }) => void,
  onUpdateEmailSettings: (data: EmailControlSettings) => void,
};

const SecuritySettings = (props: Props) => {
  const identityFormRef = useRef(null);
  const emailFormRef = useRef(null);
  const [edit, setEdit] = useState(false);
  const [identitySchema, setIdentitySchema] = useState(null);
  const [isValid, setIsValid] = useState(true);
  const [formKey, setFormKey] = useState(new Date());

  useEffect(() => {
    const schema = getSchema("DataSecurityEmployeeIdentityServiceSectionDTO");
    setIdentitySchema(schema);
  }, []);

  const submitForm = () => {
    identityFormRef.current.submit();
    emailFormRef.current.submit();
  };

  const toggleEdit = () => {
    setEdit(prev => !prev);
  };

  const handleIdentitySubmit = (formData: EmployeeIdentity) => {
    props.handleIdentitySubmit({
      clientId: props.clientId,
      formData,
    });
    toggleEdit();
  };

  const handleEmailSettingsSubmit = (formData: EmailControlSettings) => {
    props.onUpdateEmailSettings({
      ...formData,
      emailDomainRestrictionWhitelist:
        props.emailControlSettings?.emailDomainRestrictionWhitelist || [],
    });
  };

  const handleError = (errors: string[]) => {
    setIsValid(errors.length === 0);
  };

  const handleCancelEdit = () => {
    setIsValid(true);
    setFormKey(new Date());
    toggleEdit();
  };

  return (
    <View gap={4}>
      <View direction="row" justifyContent="space-between">
        <Text type="h6">{props.t("SECURITY_SETTINGS")}</Text>
        <View direction="row" justifyContent="space-between" gap="4">
          {!edit ? (
            <Button materialIcon="edit" onClick={toggleEdit} size="small">
              {props.t("EDIT")}
            </Button>
          ) : (
            <View direction="row" gap="4">
              <Button color="secondary" onClick={handleCancelEdit} size="small">
                {props.t("CANCEL")}
              </Button>
              <Button
                materialIcon="replay_circle_filled"
                className="ml-2"
                onClick={submitForm}
                disabled={!isValid}
                size="small"
              >
                {props.t("SUBMIT")}
              </Button>
            </View>
          )}
        </View>
      </View>
      <View gap={6}>
        {identitySchema && (
          <SchemaForm
            key={formKey}
            ref={identityFormRef}
            formData={props.identityData}
            schema={identitySchema}
            uiSchema={identityUiSchema(props.t)}
            onSubmit={handleIdentitySubmit}
            readonly={!edit}
            onError={handleError}
            externalSubmit
            liveValidate
            boxGrid
          />
        )}
        {props.emailControlSettings && (
          <SchemaForm
            ref={emailFormRef}
            schema={props.emailSchema}
            formData={props.emailControlSettings}
            uiSchema={emailsUiSchema(props.t)}
            onSubmit={handleEmailSettingsSubmit}
            readonly={!edit}
            externalSubmit
            boxGrid
          />
        )}
      </View>

      <View>
        <BoxField
          title={props.t("MULTI_FACTOR_AUTHENTICATION")}
          description={props.t("MULTI_AUTH_DESCRIPTION")}
          formStyle
        >
          <SwitchButton active={props.mfaEnabled} onChange={props.onMfaChange} readonly={!edit} />
        </BoxField>
      </View>
    </View>
  );
};

export default localize()(SecuritySettings);
